import { FunctionalComponent } from 'preact'
import { useEffect, useState } from 'preact/hooks'
import { TextBox } from '@/components'
import { parseValidationErrors } from '@/utils/errorHandling'
import type { CompanyTrackingViewDto } from '@/types/tracking'
import type { CustomScriptFields } from '@/hooks/useCompanyTracking'
import type { ApiResponse } from '@/api/client'

/** Server-side per-field cap (OrbitalX.Business TrackingValidationConstants.CustomScriptMaxLength). */
const MAX_SCRIPT_LENGTH = 10000

/** Field key = the API's `FieldName` on 422 errors, which mirrors `nameof(CompanyTrackingUpdateDto.*)`. */
type ScriptFieldKey = keyof CustomScriptFields

const SCRIPT_FIELDS: Array<{ key: ScriptFieldKey; label: string; caption: string }> = [
  { key: 'CustomScriptHeader', label: 'Header', caption: 'Injected at the end of <head>' },
  { key: 'CustomScriptBody', label: 'Body', caption: 'Injected immediately after <body> opens' },
  { key: 'CustomScriptFooter', label: 'Footer', caption: 'Injected before </body> closes' },
]

interface CustomTrackingScriptFormProps {
  tracking: CompanyTrackingViewDto
  onSave: (scripts: CustomScriptFields) => Promise<ApiResponse<CompanyTrackingViewDto> | null>
}

/**
 * Company-wide custom tracking scripts (Header/Body/Footer, most commonly a GTM
 * container). Content is company-level; each landing page opts in via its own
 * "Include custom tracking script" toggle in Webster. Inline errors are surfaced
 * from the API's syntax/size validation on save.
 */
export const CustomTrackingScriptForm: FunctionalComponent<CustomTrackingScriptFormProps> = ({
  tracking,
  onSave,
}) => {
  const [values, setValues] = useState<CustomScriptFields>({
    CustomScriptHeader: tracking.CustomScriptHeader ?? '',
    CustomScriptBody: tracking.CustomScriptBody ?? '',
    CustomScriptFooter: tracking.CustomScriptFooter ?? '',
  })
  const [errors, setErrors] = useState<Partial<Record<ScriptFieldKey, string>>>({})
  const [saving, setSaving] = useState(false)

  // Re-seed from the server whenever the stored script content changes (initial load
  // or after a successful save). Toggling Enabled doesn't touch these fields, so an
  // in-progress edit isn't clobbered by an unrelated tracking update.
  useEffect(() => {
    setValues({
      CustomScriptHeader: tracking.CustomScriptHeader ?? '',
      CustomScriptBody: tracking.CustomScriptBody ?? '',
      CustomScriptFooter: tracking.CustomScriptFooter ?? '',
    })
    setErrors({})
  }, [tracking.CustomScriptHeader, tracking.CustomScriptBody, tracking.CustomScriptFooter])

  const isDirty =
    values.CustomScriptHeader !== (tracking.CustomScriptHeader ?? '') ||
    values.CustomScriptBody !== (tracking.CustomScriptBody ?? '') ||
    values.CustomScriptFooter !== (tracking.CustomScriptFooter ?? '')

  // Strictly over the cap (matches the API's `> CustomScriptMaxLength` rule — exactly 10,000 is valid).
  // The paste isn't truncated (enforceMaxLength=false below), so block Save until it's back under.
  const hasOverLimit = SCRIPT_FIELDS.some((f) => values[f.key].length > MAX_SCRIPT_LENGTH)

  const handleChange = (key: ScriptFieldKey, next: string) => {
    setValues((prev) => ({ ...prev, [key]: next }))
    // Clear a field's error as soon as the user edits it.
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev))
  }

  const handleSave = async () => {
    setSaving(true)
    setErrors({})
    const res = await onSave(values)
    setSaving(false)

    // Map API field-level validation errors (FieldName = CustomScriptHeader/Body/Footer)
    // onto the matching inputs. Non-422 failures are handled by the hook via toast.
    if (res && res.status === 422) {
      const next: Partial<Record<ScriptFieldKey, string>> = {}
      for (const err of parseValidationErrors(res)) {
        if (err.FieldName && err.FieldName in values) {
          next[err.FieldName as ScriptFieldKey] = err.Message
        }
      }
      setErrors(next)
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Custom Tracking Scripts
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
          Company-wide scripts (e.g. Google Tag Manager) injected into every landing page whose
          &ldquo;Include custom tracking script&rdquo; toggle is on.
        </p>
      </div>

      {SCRIPT_FIELDS.map(({ key, label, caption }) => (
        <TextBox
          key={key}
          name={key}
          label={label}
          multiline
          rows={4}
          value={values[key]}
          onChange={(next) => handleChange(key, next)}
          error={errors[key]}
          helperText={caption}
          showCharCounter
          maxLength={MAX_SCRIPT_LENGTH}
          enforceMaxLength={false}
          placeholder="<!-- Paste your script tag here -->"
          className="font-mono text-xs"
          disabled={saving}
        />
      ))}

      <div className="flex items-center justify-end gap-3">
        {hasOverLimit && (
          <p className="text-xs text-red-600 dark:text-red-400">
            Scripts must be 10,000 characters or fewer.
          </p>
        )}
        <button
          type="button"
          onClick={handleSave}
          disabled={!isDirty || saving || hasOverLimit}
          className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving…' : 'Save scripts'}
        </button>
      </div>
    </div>
  )
}
